import type { FileFacts, Language, ReviewHandover } from '@eformulieren/engine'
import { Event, type Entries, type ViewMode } from '@eformulieren/model'
import {
  AdminQuery,
  type EarlierVersie,
  type VersionMoment,
  FillForm,
  BusyButton,
  LoginChoice,
  SessionExpired,
  SessionWarning,
  positionIn,
  type UploadOutcome,
  type ShareStart,
  type AdviceTarget,
  type SidePanel,
  type SidePanelContext,
  type CommentDeleteRequest,
  type CommentRequest,
  type LoginOption,
} from '@eformulieren/renderer'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { noticeAdminBar, outcomeFromOpening } from './admin.js'
import { fetchFacts, uploadFile } from './attachments.js'
import { ShareButton } from './ShareButton.js'
import { useAdvice } from './useAdvice.js'
import { useSharingOverview } from './useSharingOverview.js'
import { personNameFrom } from './sharing.js'
import { deleteComment, postComment } from './comments.js'
import { postConsent } from './consents.js'
import { BrandAndPath, ProductBar } from './ProductBar.js'
import { appearanceOf } from './appearance.js'
import { closedNotice } from './closed.js'
import { CopyBox } from './CopyBox.js'
import { accessEndedText, copyableText } from './accessEnded.js'
import { originRejectedText } from './originRejected.js'
import { sectionsWaitingNotice } from './rateLimited.js'
import { type Connection, connect } from './connection.js'
import { getWhoami } from './contextState.js'
import { type FormContext, instantieFromPath, tabStorage } from './formContext.js'
import { ENTRIES_RETRY_FORM, getEntries, getEntriesWithRetry } from './login.js'
import {
  AdminQueryError,
  type ContextInfo,
  NoAccessError,
  WRITE_HEADERS,
  type WerkverzamelingBody,
  getContextInfo,
  getProjection,
  getVersie,
  getVersiemomenten,
  getVersies,
  getWerkverzameling,
  headersFor,
} from './podRequests.js'
import {
  SessionExpiredError,
  isSessionExpiredResponse,
  readOnlyAfterExpiry,
  otherSideOptions,
  reloginOptions,
  reloginTarget,
  stashReturnPlace,
  takeReturnPlace,
  waitUntilSaved,
} from './session.js'
import { renewOptions } from './logout.js'
import { LogoutButton } from './LogoutButton.js'
import { targetKeepingPrefill } from './prefillStash.js'
import {
  SESSION_EXPIRED_CODE,
  SWITCH_NOT_STORED,
  logRejection,
  noticeForRejection,
  rejectionOf,
  rejectionWithoutResponse,
} from './rejection.js'

/**
 * De pagina die de publicatie-pod zelf serveert (ontwerp 3.2, B2, 11.1, R-101).
 *
 * **Tot M5.15 stond dit in het harnas van de renderer**, als de kale gastheer die een pagina
 * bedraadde omdat de pod er geen had: hij was API-only (9.2), en wie het formulier op een scherm
 * wilde deed dat zelf. Dat is nu niet meer waar. De pod serveert het formulier van zijn eigen
 * image-digest, en dan hoort de bedrading bij de pod en niet bij een dev-pagina in een ander
 * pakket. Het harnas is daarmee weer wat het was: een lokale pagina zonder netwerk.
 *
 * **De pagina kent maar één oorsprong: die van zichzelf.** Er staat geen pod-adres in de
 * context en er valt er dus geen op te geven. Dat is geen gemak maar de vorm die klopt: de pod
 * stuurt geen CORS-headers en zet `frame-ancestors` plus een herkomstcontrole op de websocket,
 * dus hij gaat ervan uit dat de pagina die hem aanroept bij hemzelf hoort. Nu doet ze dat ook.
 * De `/pod`-proxy in het harnas bestond alleen om die ene oorsprong na te bootsen en is weg.
 *
 * **Het token staat niet in de URL.** Een JWT in een adresbalk landt in geschiedenis, logs en
 * verwijzingen. Playwright zet de context met `addInitScript` op `window`, een mens zet hem in
 * `sessionStorage`; beide blijven bij deze ene pagina. Op een echte directe link, zonder een van
 * beide, staat er niets op `window` en draagt de browser het token in het HttpOnly-sessiecookie
 * dat `apps/inlog` na het inloggen heeft gezet; dat cookie is het antwoord op de vraag die hier
 * tot M5.16d openstond, en zie `token` in `formContext.ts` voor hoe de twee wegen zich verhouden. Niet het
 * postMessage-protocol van een gastheer, want die is met besluit A vervallen.
 */

/**
 * Of het formulier er is. Alleen de vraag of het opkwam: een handeling die de pod weigert staat
 * hier niet in maar in `Rejection` hieronder, want dan staat het formulier er wél.
 *
 * `beeindigd` is de eindstand na een sluiting met `geen-recht` (`toegang.ts`, ontwerp 6.4): de
 * toegang is ingetrokken of weggehaald terwijl het formulier openstond. Dan staat er geen formulier
 * meer, want een formulier dat blijft staan lijkt bewerkbaar terwijl er niets meer aankomt, en de
 * pagina verbindt niet opnieuw.
 */
/**
 * `herkomst`: de pod weigerde de samenwerkverbinding omdat deze pagina van een oorsprong komt die niet in
 * zijn lijst staat (`originRejected.ts`). Geen kwestie van toegang, en eindstand.
 */
type State = 'wachten' | 'gereed' | 'beeindigd' | 'geen-toegang' | 'beheervraag' | 'herkomst' | { readonly fout: string }

/**
 * Een handeling die niet doorging, terwijl het formulier gewoon staat (`rejection.ts`).
 *
 * Alleen de code, en de tekst pas bij het tonen, zodat de melding meewisselt met de taal (R-144).
 * Het volgnummer maakt twee keer dezelfde weigering na elkaar tot twee meldingen, zodat een
 * schermlezer de tweede ook voorleest.
 */
interface Rejection {
  readonly code: string | undefined
  readonly volgnummer: number
}

/**
 * Een beurt die deze kijker op deze pagina heeft gesloten (R-173).
 *
 * Met de instantie erbij, zodat de melding na een sprong in de reeks niet bij het volgende
 * dossier blijft staan: hij hoort bij de instantie waarop de beurt sloot.
 */
interface Closed {
  readonly instantieId: string
  readonly gebeurtenis: Event
  readonly revisieId: string
}

/** Zie de prop `adviseur` van {@link FormPage}. */
export interface AdviserMode {
  readonly anker: AdviceTarget | undefined
  /** Het label van de tweede weergave op een smal scherm: "Advies". */
  readonly knop: string
  readonly paneel: (context: SidePanelContext) => ReactNode
  readonly onEinde: () => void
  /** Een vervolgvraag sinds de vorige lezing, voor de live regio van het formulier. */
  readonly melding?: string
}

export function FormPage({
  context,
  taal: language,
  onLanguage,
  mode,
  adviseur: adviser,
}: {
  readonly context: FormContext
  readonly taal: Language
  readonly onLanguage: (language: Language) => void
  /** Een expliciete gedaante; zonder deze waarde volgt hij per instantie uit de instantie. */
  readonly mode?: ViewMode
  /**
   * De adviseurpagina na "geen belang" (R-184, R-184c, ontwerp 7.4a, mockup 05): dezelfde verbinding en
   * hetzelfde formulier, met alleen wat de pod laat zien (de gedeelde secties, alleen lezen), zonder
   * de knop voor delen, met het antwoordpaneel ernaast en het anker vast gemarkeerd. Eindigt de toegang
   * terwijl het formulier openstaat, dan meldt `onEinde` dat en zegt de adviseurpagina waarom.
   */
  readonly adviseur?: AdviserMode
}): ReactNode {
  /**
   * Welke instantie er nu openstaat, en waarom dat in de stand zit (R-122).
   *
   * Een sprong binnen de reeks is geen nieuwe pagina maar een andere instantie in dezelfde: de
   * publicatie is dezelfde, dus de definitie, de renderer en de sockets zijn dat ook. Het effect
   * hieronder hangt aan dit id, dus het bouwt de verbindingen af en opnieuw op. Herladen zou ook
   * kunnen en is slechter: dan is de context weg die de gastheer op `window` heeft gezet.
   */
  const [instantieId, setInstantieId] = useState(context.instantieId)
  const [jumping, setJumping] = useState(false)
  const current = useMemo(() => ({ ...context, instantieId }), [context, instantieId])
  const position = positionIn(context.reeks, instantieId)

  const [connection, setConnection] = useState<Connection | undefined>(undefined)
  const [state, setState] = useState<State>('wachten')
  /**
   * Wat de opslag weet van de bijlagen in de stand, per hash (R-104). Met deze kaart rekent het formulier
   * `bestandseisen` uit zoals de indienpoort; een upload zet zijn eigen bestand er meteen bij, en een hash
   * die het formulier niet kent vraagt de pagina één keer op.
   */
  const [files, setFiles] = useState<ReadonlyMap<string, FileFacts>>(new Map())
  const askedFiles = useRef(new Set<string>())
  useEffect(() => {
    setFiles(new Map())
    askedFiles.current = new Set()
  }, [instantieId])
  const uploadAttachment = async (fieldId: string, file: File): Promise<UploadOutcome> => {
    const out = await uploadFile({ instantieId: current.instantieId, veld: fieldId, file, headers: headersFor(current) })
    const facts = out.feiten
    if ('verwijzing' in out.uitkomst && facts !== undefined) {
      const hash = out.uitkomst.verwijzing.hash
      askedFiles.current.add(hash)
      setFiles((previous) => new Map([...previous, [hash, facts]]))
    }
    return out.uitkomst
  }
  const onUnknownFiles = (hashes: readonly string[]): void => {
    const fresh = hashes.filter((hash) => !askedFiles.current.has(hash))
    if (fresh.length === 0) return
    for (const hash of fresh) askedFiles.current.add(hash)
    void fetchFacts({ instantieId: current.instantieId, hashes: fresh, headers: headersFor(current) }).then((found) => {
      if (found.size > 0) setFiles((previous) => new Map([...previous, ...found]))
    })
  }
  const [rejection, setRejection] = useState<Rejection | undefined>(undefined)
  const reject = (code: string | undefined): void =>
    setRejection((previous) => ({ code, volgnummer: (previous?.volgnummer ?? 0) + 1 }))
  /**
   * De melding schuift in beeld als hij verschijnt, en alleen dan: de actiebalk staat onderaan een
   * lang formulier, en zonder dit valt de melding net onder de vouw. Een effect op het volgnummer
   * en geen callback-ref in de JSX, want die draait bij elke render opnieuw en trekt wie na de
   * melding omhoog scrolt bij de volgende taalwissel terug naar beneden.
   */
  const rejectionNotice = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    if (rejection !== undefined) rejectionNotice.current?.scrollIntoView({ block: 'nearest' })
  }, [rejection?.volgnummer])
  /**
   * De beurt die hier net is gesloten, of niets (R-173, R-86).
   *
   * Niet teruggezet door het laadeffect hieronder: valt de pagina na een geslaagde overdracht
   * terug op opnieuw laden (`reload`), dan hoort de melding dat te overleven.
   */
  const [closed, setClosed] = useState<Closed | undefined>(undefined)
  /**
   * Telt op als de pagina na een geslaagde overdracht de projectie niet opnieuw kon halen, zodat
   * het laadeffect alles opnieuw opbouwt. Dat is de terugval van {@link afterClose}, niet de
   * gewone weg; zie daar waarom.
   */
  const [reload, setReload] = useState(0)
  const [work, setWork] = useState<WerkverzamelingBody | undefined>(undefined)
  /** De eerdere versies van deze aanvraag, en welke er nu naast staat (R-96, R-174). */
  const [versies, setVersies] = useState<readonly EarlierVersie[]>([])
  /** De standen van deze beurt, voor het paneel Geschiedenis; opnieuw opgehaald als dat paneel opengaat. */
  const [versiemomenten, setVersiemomenten] = useState<readonly VersionMoment[] | undefined>(undefined)
  const [chosenVersie, setChosenVersie] = useState<string | undefined>(undefined)
  const [earlierValues, setEarlierValues] = useState<ReadonlyMap<string, unknown> | undefined>(
    undefined,
  )
  /** De contextkop van deze instantie: dossier, uitvraagmoment en deadline (R-125, R-128). */
  const [contextInfo, setContextInfo] = useState<ContextInfo>({})
  /** Tot wanneer deze kijker als beheerder leest, of niets (R-155f). */
  const [adminUntil, setAdminUntil] = useState<string | undefined>(undefined)
  /**
   * De sessie is verlopen (R-154, ontwerp 6.4, `session.ts`), en of er dan nog iets onbevestigd is.
   *
   * Niet teruggezet door het laadeffect: een verlopen sessie hoort bij de pagina en niet bij één
   * instantie, en wat er dan nog gebeurt (een sprong, een herlaadslag na een overdracht) faalt op
   * hetzelfde.
   */
  const [sessionExpired, setSessionExpired] = useState(false)
  /**
   * De dialoog voor delen en advies vragen: dicht, of open met deze start (`ShareButton`). Hier en
   * niet in de knop, zodat ook het adviespaneel en de tekstballon bij een vraag hem kunnen openen,
   * met `{ rol: 'advies', anker }`.
   */
  const [shareStart, setShareStart] = useState<ShareStart | undefined>(undefined)
  /** De secties die nu verborgen zijn, voor "Alle secties tonen" in de dialoog (`onHiddenSections`). */
  const [hiddenSections, setHiddenSections] = useState<readonly string[]>([])
  /**
   * De waarschuwing vóór het verlopen (WCAG 2.2.1, besluit van 30 oktober 2026): vijf minuten voor
   * de vervaltijd uit `/whoami`. Zonder vervaltijd (een token van de gastheer in een header) is er
   * geen waarschuwing; dan volgt bij verlopen de melding van hierna.
   */
  const [sessionWarning, setSessionWarning] = useState<'nee' | 'open' | 'later'>('nee')
  /** Of "Sessie verlengen" nog wacht tot alles is opgeslagen, of al doorstuurt. */
  const [renewing, setRenewing] = useState<'opslaan' | 'doorsturen' | undefined>(undefined)
  /** Lukte het opslaan niet op tijd, dan zegt de waarschuwing dat. */
  const [renewNotice, setRenewNotice] = useState<string | undefined>(undefined)
  const expiresAt = current.verlooptOp === undefined ? undefined : current.verlooptOp * 1000
  useEffect(() => {
    if (expiresAt === undefined) return
    const wait = Math.min(Math.max(0, expiresAt - SESSION_WARNING_MS - Date.now()), MAX_TIMER_MS)
    const timer = setTimeout(() => setSessionWarning((previous) => (previous === 'nee' ? 'open' : previous)), wait)
    return () => clearTimeout(timer)
  }, [expiresAt])
  const [unsaved, setUnsaved] = useState(false)
  const unsavedRef = useRef(false)
  unsavedRef.current = unsaved
  const languageRef = useRef(language)
  languageRef.current = language
  const [lostText, setLostText] = useState<string | undefined>(undefined)
  /** Wanneer dit venster de sluiting kreeg die alle toegang beëindigde (voor de pagina van G-01). */
  const [endedAt, setEndedAt] = useState<string | undefined>(undefined)
  /**
   * Wat er aan de toegang veranderde terwijl het formulier openstond (besluit 9, G-02): secties die
   * wegvielen (4006) of naar lezen gingen, met de niet-opgeslagen tekst daarvan om te kopiëren.
   */
  const [accessChanges, setAccessChanges] = useState<readonly { readonly sectie: string; readonly soort: 'weg' | 'lezen'; readonly tekst: string }[]>([])
  /** De secties die wachten op de grens per gebruiker en het intussen opnieuw proberen (`rateLimited.ts`). */
  const [waitingSections, setWaitingSections] = useState<readonly string[]>([])
  /**
   * De secties die nu van generatie wisselen (eisenboek 12.50, `generation.ts`): het oude document blijft in
   * beeld tot het nieuwe er is, alleen-lezen, zodat niemand typt in iets dat de pod niet meer aanneemt.
   */
  const [switchingSections, setSwitchingSections] = useState<readonly string[]>([])
  /**
   * Eigen invoer die na een generatienaad niet terug kon, omdat een ander intussen in hetzelfde veld schreef
   * (`carryOverAcrossSeam`): per sectie de velden en hun tekst om te kopiëren. Nooit stil weg.
   */
  const [seamNotices, setSeamNotices] = useState<readonly { readonly sectie: string; readonly velden: readonly string[]; readonly tekst: string }[]>([])
  /**
   * Een oordeel dat op de 401 strandde. Het reist in het lijf van `PUT /criteria` en niet door een
   * sectiedocument, dus de tracker ziet het niet; zonder dit zou de melding zeggen dat alles is
   * opgeslagen terwijl dat oordeel er niet is.
   */
  const [verdictLost, setVerdictLost] = useState(false)
  /** Of het formulier op deze pagina ooit is opgekomen: dan is de voorvulling verstuurd (`reloginTarget`). */
  const formWasReady = useRef(false)
  const [entries, setEntries] = useState<Entries | undefined>(undefined)
  /** Waar "Opnieuw inloggen" heen gaat, vastgesteld op het moment van verlopen. */
  const [reloginHref, setReloginHref] = useState<string | undefined>(undefined)
  /** De verbinding van het laadeffect, zodat een 401 op een handeling haar kan laten verlopen. */
  const live = useRef<Connection | undefined>(undefined)
  const expiredOnce = useRef(false)
  const expireSession = (): void => {
    live.current?.expire()
    if (expiredOnce.current) return
    expiredOnce.current = true
    // Alleen-lezen, op dezelfde documenten en editors: niemand typt door in iets dat niet meer
    // aankomt, en de tekst blijft te selecteren en te kopiëren (ontwerp 6.4, R-166).
    setConnection((open) => (open === undefined ? open : { ...open, projectie: readOnlyAfterExpiry(open.projectie) }))
    // Kwam het formulier nooit op, dan is de voorvulling nog niet verstuurd en gaat ze mee terug, maar
    // alleen voor de instantie uit het pad: bij een andere hoort ze niet (`reloginTarget`).
    const base = reloginTarget(window.location, current.instantieId)
    const prefill = new URLSearchParams(current.voorvulling).toString()
    setReloginHref(
      !formWasReady.current && current.instantieId === instantieFromPath() && prefill !== ''
        ? targetKeepingPrefill(base, `?${prefill}`, current.instantieId, tabStorage(), Date.now())
        : base,
    )
    setSessionExpired(true)
    // De ingangen opnieuw: ze kunnen bij het opkomen niet zijn gelezen. Een mislukte poging laat staan
    // wat er al was.
    void getEntries().then((read) => {
      if (read !== undefined) setEntries(read)
    })
  }

  /**
   * De ingangen bij het opkomen: voor "Uitloggen", opnieuw inloggen en de andere kant (R-154), begrensd
   * opnieuw geprobeerd (`getEntriesWithRetry`). "Sessie verlengen" hangt er niet meer van af: dat kan ook
   * langs de eigen ingang uit `/whoami` (`current.ingang`, WCAG 2.2.1, R-139).
   */
  useEffect(() => {
    let gone = false
    void getEntriesWithRetry(ENTRIES_RETRY_FORM, { cancelled: () => gone }).then((read) => {
      if (!gone && read !== undefined) setEntries(read)
    })
    return () => {
      gone = true
    }
  }, [])

  // Nog eens als de waarschuwing opengaat en de ingangen er nog niet zijn: de pod kan intussen weer
  // antwoorden, en dan biedt de waarschuwing de ingangen in plaats van alleen de eigen.
  const warningOpen = sessionWarning === 'open'
  const entriesKnown = entries !== undefined
  useEffect(() => {
    if (!warningOpen || entriesKnown) return
    let gone = false
    void getEntriesWithRetry(ENTRIES_RETRY_FORM, { cancelled: () => gone }).then((read) => {
      if (!gone && read !== undefined) setEntries(read)
    })
    return () => {
      gone = true
    }
  }, [warningOpen, entriesKnown])

  /**
   * "Sessie verlengen": eerst alles opgeslagen, dan de plek bewaren, dan naar `verlengen` bij de
   * inlogdienst (WCAG 2.2.1, R-139, R-154). Die gaat langs de identiteitsprovider en stuurt terug naar
   * deze instantie; de pagina laadt dan opnieuw en de invoer komt uit de opslag. Wat er onbevestigd
   * is zou bij die navigatie weg zijn, en daarom wacht de knop tot de tracker leeg is.
   */
  const renew = (address: string): void => {
    if (renewing !== undefined) return
    setRenewing('opslaan')
    setRenewNotice(undefined)
    void waitUntilSaved(() => unsavedRef.current, { maxMs: RENEW_WAIT_MS, stepMs: 200 }).then((saved) => {
      if (!saved) {
        setRenewing(undefined)
        setRenewNotice(RENEW_NOT_SAVED[languageRef.current])
        return
      }
      stashReturnPlace(tabStorage(), current.instantieId, window.scrollY, Date.now())
      setRenewing('doorsturen')
      window.location.assign(address)
    })
  }

  useEffect(() => {
    let cleaned = false
    let connected: Connection | undefined
    // Terug naar wachten bij een sprong: nooit het formulier van het vorige dossier laten staan
    // terwijl het volgende nog opkomt (valkuil 6).
    setState('wachten')
    setConnection(undefined)
    setWork(undefined)
    setVersies([])
    setVersiemomenten(undefined)
    setChosenVersie(undefined)
    setEarlierValues(undefined)
    setContextInfo({})
    setRejection(undefined)
    setAdminUntil(undefined)
    void (async () => {
      /**
       * De toegang is beëindigd terwijl het formulier openstond: de verbindingen weg, zodat de
       * provider niet opnieuw verbindt, en het formulier uit beeld (ontwerp 6.4). Eén keer, want
       * elke sectie op dezelfde socket hoort dezelfde sluiting.
       */
      let ended = false
      setAccessChanges([])
      setWaitingSections([])
      setSwitchingSections([])
      setSeamNotices([])
      const endAccess = (): void => {
        if (cleaned || ended) return
        ended = true
        setEndedAt(new Date().toISOString())
        // Wat nog niet was bevestigd, om te kopiëren op de pagina die nu komt (audit delen).
        if (connected !== undefined && unsavedRef.current) {
          // Alleen de secties waar nog iets onbevestigd stond; weet de verbinding dat niet meer, alles.
          const open = connected
          const pending = new Set(open.projectie.secties.filter((section) => open.unsavedIn(section.id)).map((section) => section.id))
          setLostText(copyableText(open.projectie, open.documenten, languageRef.current, pending.size === 0 ? undefined : pending))
        }
        connected?.dispose()
        setConnection(undefined)
        setState('beeindigd')
      }
      /**
       * De pod weigerde de socket om de oorsprong van deze pagina (`originRejected.ts`): net als bij het
       * einde van de toegang de verbinding weg en het formulier uit beeld, met wat nog niet was bevestigd
       * om te kopiëren, maar met een eigen melding. Het is een instelling van de pod, geen toegang.
       */
      const rejectOrigin = (): void => {
        if (cleaned || ended) return
        ended = true
        logRejection({ handeling: 'samenwerkverbinding', reden: 'herkomst-niet-toegestaan', oorsprong: window.location.origin })
        if (connected !== undefined && unsavedRef.current) {
          const open = connected
          const pending = new Set(open.projectie.secties.filter((section) => open.unsavedIn(section.id)).map((section) => section.id))
          setLostText(copyableText(open.projectie, open.documenten, languageRef.current, pending.size === 0 ? undefined : pending))
        }
        connected?.dispose()
        setConnection(undefined)
        setState('herkomst')
      }
      try {
        const { projectie: projection, verwijzingen, grond: appearanceBasis, beheerTot: until } = await getProjection(current)
        if (cleaned) return
        setAdminUntil(until)
        const fetched = await getWerkverzameling(current)
        if (cleaned) return
        setWork(fetched)
        setVersies(await getVersies(current))
        if (cleaned) return
        setVersiemomenten(await getVersiemomenten(current))
        if (cleaned) return
        setContextInfo(await getContextInfo(current))
        if (cleaned) return
        // Per instantie en niet één keer per pagina: na een sprong in de reeks is het een andere
        // instantie, en die kan in een andere beurt staan.
        /**
         * Een sluiting met `token-verlopen` is zeker; een geweigerde handdruk kan ook een ingetrokken
         * recht zijn of de begrenzing per gebruiker, dus dan eerst `/whoami`: alleen een 401 daar is
         * een verlopen sessie. Bij iets anders verandert er niets aan wat de pagina al deed.
         */
        const onSession = (certain: boolean): void => {
          if (cleaned || ended) return
          if (certain) {
            expireSession()
            return
          }
          void getWhoami().then((outcome) => {
            if (!cleaned && !ended && outcome.soort === 'geen-sessie') expireSession()
          })
        }
        /**
         * Een sectie die wegvalt of naar lezen gaat (besluit 9): eerst de tekst die er nog niet was
         * bevestigd, om te kopiëren, dan de sectie uit `save()`, en de sectie alleen-lezen op dezelfde
         * documenten, zodat niemand doortypt in iets dat niet meer aankomt. De andere secties werken door.
         * Op de adviseurpagina eindigt de toegang dan helemaal: daar is geen deel dat doorwerkt.
         */
        const changeAccess = (sectionId: string, kind: 'weg' | 'lezen'): void => {
          if (cleaned || ended || connected === undefined) return
          const open = connected
          const text = open.unsavedIn(sectionId) ? copyableText(open.projectie, open.documenten, languageRef.current, new Set([sectionId])) : ''
          open.release(sectionId, kind === 'weg')
          setAccessChanges((before) => [...before.filter((change) => change.sectie !== sectionId), { sectie: sectionId, soort: kind, tekst: text }])
          setConnection((shown) =>
            shown === undefined
              ? shown
              : {
                  ...shown,
                  projectie: {
                    ...shown.projectie,
                    secties: shown.projectie.secties.map((section) => (section.id === sectionId ? { ...section, recht: 'lezen' as const } : section)),
                  },
                },
          )
        }
        connected = connect(
          current,
          projection,
          verwijzingen,
          appearanceOf(appearanceBasis, mode),
          endAccess,
          onSession,
          setUnsaved,
          adviser === undefined ? (sectionId) => changeAccess(sectionId, 'weg') : undefined,
          (sectionId) => (adviser === undefined ? changeAccess(sectionId, 'lezen') : endAccess()),
          rejectOrigin,
          (sectionId, waiting) => {
            if (cleaned) return
            setWaitingSections((before) => {
              const without = before.filter((id) => id !== sectionId)
              return waiting ? [...without, sectionId] : without
            })
          },
          /**
           * Een generatienaad (eisenboek 12.50): de sectie blijft alleen-lezen in beeld tot haar nieuwe document
           * er is, dan wisselt het formulier van document. De versiemomenten horen bij de generatie die sloot
           * (`GET /versiemomenten` levert die van de open generatie), dus in hetzelfde moment weg en opnieuw
           * opgehaald: een moment van de oude generatie past niet op het nieuwe document, en de modus met
           * historie valt daarmee terug op nu.
           */
          (sectionId, event) => {
            if (cleaned || connected === undefined) return
            const open = connected
            if (event.soort === 'wisselt') {
              setSwitchingSections((before) => (before.includes(sectionId) ? before : [...before, sectionId]))
              return
            }
            setSwitchingSections((before) => before.filter((id) => id !== sectionId))
            setVersiemomenten(undefined)
            setConnection((shown) => (shown === undefined ? shown : { ...shown, documenten: new Map(open.documenten), verbindingen: new Map(open.verbindingen) }))
            if (event.nietTeruggezet.length > 0) {
              const text = copyableText(open.projectie, new Map([[sectionId, event.oud]]), languageRef.current, new Set([sectionId]), new Set(event.nietTeruggezet))
              setSeamNotices((before) => [...before.filter((notice) => notice.sectie !== sectionId), { sectie: sectionId, velden: event.nietTeruggezet, tekst: text }])
            }
            void getVersiemomenten(current)
              .then((moments) => {
                if (!cleaned) setVersiemomenten(moments)
              })
              .catch((error: unknown) => logRejection({ handeling: 'versiemomenten', fout: error }))
          },
        )
        live.current = connected
        setConnection(connected)
        await connected.save()
        if (!cleaned && !ended) {
          // Na een verlenging terug op de plek waar de invuller stond (`stashReturnPlace`).
          if (!formWasReady.current) {
            const place = takeReturnPlace(tabStorage(), current.instantieId, Date.now())
            if (place !== undefined) restoreScroll(place)
          }
          formWasReady.current = true
          setState('gereed')
          setJumping(false)
        }
      } catch (error) {
        if (cleaned || ended) return
        if (error instanceof SessionExpiredError) {
          expireSession()
          return
        }
        if (error instanceof NoAccessError) {
          setState('geen-toegang')
          return
        }
        // De keten naar het log, en op het scherm een zin en geen ruw antwoord (ontwerp 11.4).
        if (!(error instanceof AdminQueryError)) logRejection({ handeling: 'laden', fout: error })
        setState(error instanceof AdminQueryError ? 'beheervraag' : { fout: error instanceof Error ? error.message : String(error) })
      }
    })()
    return () => {
      cleaned = true
      if (live.current === connected) live.current = undefined
      connected?.dispose()
    }
  }, [current, mode, reload])

  /**
   * Een beurt sluiten langs de pod: indienen (`POST /indienen`) of een toetsoverdracht
   * (`POST /overdrachten/:gebeurtenis`, 7.8 voorstel 1).
   *
   * **Eerst alles wegschrijven.** De pod bevriest de laag met wat hij heeft (`overdracht.ts` stap
   * 2); een opmerking voor de aanvrager die net voor "Terugleggen" is getypt (R-75) en nog niet
   * bij de pod is, zou anders niet in de revisie staan. Dezelfde regel als bij indienen en bij
   * een sprong in de reeks.
   *
   * De CSRF-header hoort erbij zoals bij elke schrijfactie met het cookie. Een weigering laat het
   * formulier staan met een melding bij de actiebalk (`rejection.ts`); na een geslaagde
   * overdracht staat hetzelfde formulier er alleen-lezen ({@link afterClose}).
   *
   * **Het woord in de melding komt uit het antwoord van de pod**, uit de revisie die hij net
   * schreef. Bij indienen kiest de pod tussen `ingediend` en `opnieuw-ingediend` (R-173,
   * `eventOnSubmit` in de engine); de pagina stuurt in beide gevallen hetzelfde verzoek
   * en weet niet welke van de twee het wordt. `verwacht` is alleen wat ze toont als het antwoord
   * geen leesbare gebeurtenis draagt, en dat hoort niet voor te komen.
   */
  async function closeBeurt(
    connected: Connection,
    path: string,
    expected: Event,
  ): Promise<void> {
    try {
      await connected.save()
      const response = await fetch(path, {
        method: 'POST',
        headers: {
          ...headersFor(current),
          // Een schrijfactie met het cookie draagt een eigen header (CSRF); zie `CLIENT_HEADER`.
          ...WRITE_HEADERS,
        },
        body: JSON.stringify({ instantie: current.instantieId }),
      })
      if (response.status === 201) {
        const body = (await response.json()) as { revisie: { id: string; gebeurtenis?: unknown } }
        const recorded = Event.safeParse(body.revisie.gebeurtenis)
        const eventType = recorded.success ? recorded.data : expected
        setRejection(undefined)
        setClosed({ instantieId: current.instantieId, gebeurtenis: eventType, revisieId: body.revisie.id })
        await afterClose(connected)
        return
      }
      if (isSessionExpiredResponse(response.status)) {
        expireSession()
        // De invoer is weggeschreven (`save()` hierboven), de handeling zelf niet: dat zegt de
        // melding bij de actiebalk, want de melding bovenaan gaat over de invoer.
        reject(await rejectionOf('POST', path, response))
        return
      }
      reject(await rejectionOf('POST', path, response))
    } catch (error) {
      // Wegschrijven kon niet meer: de melding bovenaan staat er al, en deze zegt dat de handeling
      // niet doorging.
      if (error instanceof SessionExpiredError) {
        reject(SESSION_EXPIRED_CODE)
        return
      }
      reject(rejectionWithoutResponse('POST', path, error))
    }
  }

  /**
   * Na een geslaagde overdracht: dezelfde documenten, nu alleen-lezen (R-86, R-133, valkuil 6).
   *
   * De pod archiveert bij het sluiten de generaties van de laag en sluit de verbindingen
   * (`overdracht.ts` stap 4). Wat er daarna in deze browser wordt getypt komt nergens meer aan.
   *
   * **De documenten houden, en niet alles opnieuw laden.** De documenten in deze browser zijn
   * precies de stand die de pod net heeft bevroren. Een verse verbinding krijgt die stand niet
   * terug: voor een gearchiveerde laag opent de pod een nieuwe generatie, gezaaid uit de revisie
   * van de voorganger en niet uit die van deze beurt (`zorgVoorGeneratie` in `generaties.ts`).
   * Gemeten met de terugleg-flow: toen deze functie nog alles opnieuw laadde, was de opmerking
   * die de toetser net schreef weg. Het gedrag van de pod zelf valt buiten deze opdracht en staat
   * in `docs/dekking.md`.
   *
   * **De verbindingen gaan wel weg.** Niets wat ze nog zouden synchroniseren komt bij de pod aan,
   * en volgens de code (`onLoadDocument` in `samenwerken.ts`) opent een handdruk op een document
   * dat de pod niet meer in geheugen heeft zo'n nieuwe generatie op een bevroren instantie. Dat
   * ze de lokale stand overschrijven is niet gezien: met de verbindingen acht seconden open bleef
   * de opmerking staan. Ze gaan weg omdat er niets meer is om te synchroniseren.
   *
   * **Dan de projectie opnieuw, van de pod.** De instantie is bevroren, dus de gedaante wordt
   * `nalezen` (`gedaante.ts` regel 2) en elke sectie alleen-lezen (`rolRecht`); de pagina leidt
   * dat niet zelf af. Lukt dat verzoek niet, dan laadt de pagina alles opnieuw (`reload`): de
   * beurt is gesloten en wat de pod zegt gaat voor.
   */
  async function afterClose(connected: Connection): Promise<void> {
    connected.dispose()
    try {
      const { projectie: projection, verwijzingen, grond: appearanceBasis } = await getProjection(current)
      setConnection({
        ...connected,
        projectie: projection,
        verwijzingen,
        modus: appearanceOf(appearanceBasis, mode),
        verbindingen: new Map(),
        save: async () => {},
        dispose: () => {},
      })
      const fetched = await getWerkverzameling(current)
      if (fetched !== undefined) setWork(fetched)
      setVersies(await getVersies(current))
    } catch (error) {
      logRejection({ handeling: 'na-sluiten', fout: error })
      setReload((previous) => previous + 1)
    }
  }

  /**
   * De melding na het sluiten, als regio die er altijd staat.
   *
   * `role="status"` en altijd in de pagina, ook tijdens het wachtscherm: een schermlezer leest een
   * statusregio voor als haar inhoud verandert, niet als ze er met inhoud en al bij komt.
   */
  const closedHere = closed !== undefined && closed.instantieId === current.instantieId ? closed : undefined
  const statusLine = (
    <p
      className={closedHere === undefined ? 'pod-beurt' : 'pod-beurt pod-beurt--gesloten'}
      role="status"
      lang={language}
      data-proef="beurt"
      data-stand={closedHere === undefined ? 'open' : 'gesloten'}
      data-gebeurtenis={closedHere?.gebeurtenis ?? ''}
      data-revisie={closedHere?.revisieId ?? ''}
    >
      {closedHere === undefined ? '' : closedNotice(closedHere.gebeurtenis, language)}
    </p>
  )

  /**
   * De productbalk staat boven elke stand van de pagina, ook tijdens het verbinden (R-157): het
   * is chrome van de pagina en niet van het formulier, dus hij hoeft niet te wachten op de
   * samenwerkverbinding. Het dossier komt mee zodra `getContextInfo` klaar is, wat vóór
   * `connect()` in de effect hierboven gebeurt; de instantietitel pas met `verbinding.projectie`.
   */
  const productBar = (
    <ProductBar
      taal={language}
      {...(contextInfo.bloknaam === undefined ? {} : { bloknaam: contextInfo.bloknaam })}
      {...(connection === undefined ? {} : { instantieTitle: connection.projectie.titel[language] })}
    />
  )

  /**
   * De melding bij een verlopen sessie (R-154, ontwerp 6.4), bovenaan en over de volle breedte, zoals
   * de beheerbalk, en daar blijvend in beeld (`pagina.css`): wie midden in een lang formulier zit, ziet
   * haar zonder te zoeken. Het formulier blijft eronder staan, zodat wat nog niet is opgeslagen er
   * staat om over te nemen. Zonder formulier in beeld (de sessie verliep bij het opkomen, of bij een
   * sprong, die eerst alles wegschrijft) zegt ze niets over de invoer.
   */
  const formInView = connection !== undefined && state === 'gereed'
  /**
   * De ingangen voor verlengen, opnieuw inloggen en uitloggen: die van `/ingangen`, en zolang die er niet
   * zijn de eigen uit `/whoami`. Alle drie hebben alleen de eigen ingang nodig; de keuze aan de andere
   * kant (`otherSideOptions`) niet, en die blijft daarom op `entries`.
   */
  const sessionEntries = entries ?? current.ingang
  const target = reloginHref ?? reloginTarget(window.location, current.instantieId)
  const sessionStand = !formInView ? 'zonder-formulier' : unsaved || verdictLost ? 'mogelijk-niet-opgeslagen' : 'opgeslagen'
  const sessionNotice = !sessionExpired ? null : (
    <div className="pod-sessie" data-proef="sessie-verlopen" data-stand={sessionStand}>
      <SessionExpired
        taal={language}
        stand={sessionStand}
        opties={reloginOptions(sessionEntries, connection?.projectie.kant, target)}
        terugval={target}
      />
    </div>
  )

  const warningNotice =
    sessionExpired || sessionWarning !== 'open' || expiresAt === undefined ? null : (
      <div className="pod-sessie" data-proef="sessie-waarschuwing">
        <SessionWarning
          taal={language}
          verlooptOp={expiresAt}
          opties={renewOptions(sessionEntries, current.persoon, connection?.projectie.kant, target)}
          onLater={() => setSessionWarning('later')}
          onVerlengen={renew}
          {...(renewing === undefined ? {} : { bezig: renewing })}
          {...(renewNotice === undefined ? {} : { melding: renewNotice })}
        />
      </div>
    )

  // Advies (R-184, R-184b): alleen aan de interne kant, en alleen als er advies is voor deze kijker.
  // Een gewone hook vóór de vroege returns hieronder; zonder projectie doet hij niets.
  const advice = useAdvice({
    taal: language,
    instantieId: current.instantieId,
    // De adviseur vraagt hier geen advies en ziet geen verzoeken van anderen.
    projectie: adviser === undefined ? connection?.projectie : undefined,
    ik: current.persoon,
    headers: headersFor(current),
    onOpenWith: setShareStart,
    onSessionExpired: expireSession,
  })

  /**
   * Het overzicht van de delingen voor het formulier zelf: de labels in de aanwezigheid en bij de
   * opmerkingen (G-05, G-19), en of de kijker via een deling binnenkwam (G-16, R-69). Aan de interne
   * kant uit de lezing van `useAdvice`, aan de externe kant uit een eigen lezing.
   */
  const internalViewer = connection?.projectie.kant === 'intern'
  const sharing = useSharingOverview({
    enabled: connection !== undefined && !internalViewer && adviser === undefined,
    instantieId: current.instantieId,
    taal: language,
    headers: headersFor(current),
    onSessionExpired: expireSession,
  })
  const overview = advice.overview ?? sharing.overview
  const askOverview = useRef(sharing.ask)
  askOverview.current = sharing.ask
  const labels = overview?.labels
  const nameOf = useMemo(() => {
    // Ook zonder lezing (de adviseurpagina, vóór de eerste lezing, of als ze mislukte): dan heet een ander
    // "Een andere deelnemer", en nooit "Deelnemer 1" of zijn sleutel (G-19).
    const known = labels ?? {}
    const named = personNameFrom(known, language)
    return (person: string): string => {
      // Iemand die het overzicht nog niet kent, net binnengekomen: binnenkort opnieuw lezen.
      if (!(person in known)) askOverview.current()
      return named(person)
    }
  }, [labels, language])
  const sharedEntry = useMemo(() => {
    if (overview?.mijnToegang !== 'deling') return undefined
    // Wie via een deling binnenkwam, ziet alleen zijn eigen regel (besluit 6).
    const inviter = overview.delingen[0]?.delerRol
    return { uitgenodigdDoor: inviter === undefined || inviter === 'u' ? undefined : inviter }
  }, [overview])

  const ended = state === 'beeindigd' || state === 'geen-toegang'
  const onAdviserEnd = adviser?.onEinde
  useEffect(() => {
    if (ended) onAdviserEnd?.()
  }, [ended, onAdviserEnd])
  const adviserPanel = useMemo<SidePanel | undefined>(
    () =>
      adviser === undefined
        ? undefined
        : { knop: adviser.knop, toon: 0, render: adviser.paneel, ...(adviser.melding === undefined ? {} : { melding: adviser.melding }) },
    [adviser],
  )

  if (sessionExpired && !formInView && state !== 'beeindigd') {
    return (
      <>
        {sessionNotice}
        {productBar}
        {statusLine}
      </>
    )
  }

  if (state === 'beheervraag') {
    /**
     * Een functioneel beheerder zonder toekenning (R-155f): de vraag of hij als beheerder wil
     * kijken, met reden en meldingsnummer. Na een geslaagde opening laadt de pagina alles opnieuw,
     * en dan komt de projectie van de opening op, alleen lezen, met de balk erboven.
     */
    return (
      <>
        {productBar}
        <div className="pod-beheer">
          <AdminQuery
            taal={language}
            onOpen={async (data) => {
              const path = '/beheer/openingen'
              try {
                const response = await fetch(path, {
                  method: 'POST',
                  headers: { ...headersFor(current), ...WRITE_HEADERS },
                  body: JSON.stringify({ instantie: current.instantieId, ...data }),
                })
                const text = await response.text()
                if (response.status !== 201) logRejection({ methode: 'POST', pad: path, status: response.status, lijf: text.slice(0, 2000) })
                // Een verlopen sessie: de melding met "Opnieuw inloggen" in plaats van de vraag.
                if (isSessionExpiredResponse(response.status)) expireSession()
                const outcome = outcomeFromOpening(response.status, text, language)
                if (outcome.soort === 'geopend') setReload((counter) => counter + 1)
                return outcome
              } catch (error) {
                logRejection({ methode: 'POST', pad: path, fout: error })
                return outcomeFromOpening(0, '', language)
              }
            }}
          />
        </div>
      </>
    )
  }

  /**
   * De balk boven een formulier dat alleen langs een opening door beheer openstaat (R-155f): als
   * beheerder, alleen lezen, tot wanneer. Bovenaan en over de volle breedte, vóór de kop, zodat hij
   * de twee rijen van de kop niet uit elkaar trekt en bij elk scrollen als eerste is gezien.
   */
  const adminBar =
    adminUntil === undefined ? null : (
      <p className="pod-beheerbalk" role="status" lang={language} data-proef="beheerbalk">
        {noticeAdminBar(adminUntil, language)}
      </p>
    )

  if (state === 'beeindigd' || state === 'geen-toegang') {
    const text = accessEndedText(state, language, state === 'beeindigd' ? endedAt : undefined)
    return (
      <>
        {productBar}
        <section className="pod-geentoegang" lang={language} data-proef={state === 'beeindigd' ? 'toegang-beeindigd' : 'geen-toegang'}>
          <h1 className="pod-geentoegang__titel">{text.titel}</h1>
          <p role="alert" className="pod-geentoegang__uitleg">
            {text.uitleg}
          </p>
          {text.andereKant === undefined ? null : <AnotherSide tekst={text.andereKant} taal={language} opties={otherSideOptions(entries, current.persoon, reloginTarget(window.location, current.instantieId))} />}
          <p>{text.wat}</p>
          {lostText === undefined || lostText === '' ? null : (
            <div className="pod-geentoegang__kopie">
              <CopyBox taal={language} label={text.kopie} uitleg={text.kopieUitleg} tekst={lostText} />
            </div>
          )}
        </section>
      </>
    )
  }

  if (state === 'herkomst') {
    const text = originRejectedText(language)
    return (
      <>
        {productBar}
        <section className="pod-geentoegang" lang={language} data-proef="herkomst-geweigerd">
          <h1 className="pod-geentoegang__titel">{text.titel}</h1>
          <p role="alert" className="pod-geentoegang__uitleg">
            {text.uitleg}
          </p>
          <p>{text.wat}</p>
          {lostText === undefined || lostText === '' ? null : (
            <div className="pod-geentoegang__kopie">
              <CopyBox
                taal={language}
                label={language === 'nl' ? 'Wat u net typte en nog niet was opgeslagen' : 'What you just typed and had not been saved yet'}
                uitleg={language === 'nl' ? 'Alleen op dit scherm, niet verstuurd. Kopieer het als u het wilt bewaren.' : 'Only on this screen, not sent. Copy it if you want to keep it.'}
                tekst={lostText}
              />
            </div>
          )}
        </section>
      </>
    )
  }

  if (typeof state === 'object') {
    // Het ruwe antwoord staat in het log (`logRejection` in `rejection.ts`) en niet op het scherm.
    return (
      <>
        {productBar}
        <p role="alert" data-proef="fout" data-oorzaak={state.fout.slice(0, 200)}>
          {language === 'nl'
            ? 'Het formulier kon niet worden geladen. Probeer het over een paar minuten opnieuw.'
            : 'The form could not be loaded. Please try again in a few minutes.'}
        </p>
        {statusLine}
      </>
    )
  }

  if (connection === undefined) {
    // Nooit een leeg formulier vóór de eerste synchronisatie (valkuil 6).
    return (
      <>
        {productBar}
        {statusLine}
        <p role="status" data-proef="wachten">
          Verbinding maken met de pod.
        </p>
      </>
    )
  }

  /**
   * De melding dat de toegang veranderde terwijl het formulier openstond (besluit 9, G-02, mockup 08):
   * boven het formulier, met de sectie bij naam, de niet-opgeslagen tekst om te kopiëren, en bij een
   * sectie die wegviel "Opnieuw laden". Opnieuw laden schrijft eerst weg wat nog werkt en haalt dan de
   * projectie opnieuw op, zonder de pagina te verversen (dan zou de context van `window` weg zijn).
   */
  const sectionTitle = (sectionId: string): string =>
    connection.projectie.secties.find((section) => section.id === sectionId)?.titel[language] ?? (language === 'nl' ? 'een sectie' : 'a section')
  const gone = accessChanges.filter((change) => change.soort === 'weg')
  const reading = accessChanges.filter((change) => change.soort === 'lezen')
  /**
   * Eigen invoer die na een generatienaad niet terug kon (eisenboek 12.50): een ander schreef intussen in
   * hetzelfde veld. De pagina zette er niets overheen; de invuller krijgt zijn tekst om te kopiëren.
   */
  const fieldLabel = (sectionId: string, fieldId: string): string => {
    const field = connection.projectie.secties.find((section) => section.id === sectionId)?.velden.find((candidate) => candidate.id === fieldId)
    return field?.label === undefined ? fieldId : field.label[language]
  }
  const seamNotice =
    seamNotices.length === 0 ? undefined : (
      <section className="pod-toegangmelding" lang={language} data-proef="generatienaad" aria-labelledby="pod-naadmelding-kop">
        <h2 id="pod-naadmelding-kop" className="pod-toegangmelding__kop">
          {language === 'nl' ? 'Niet alles kon worden teruggezet' : 'Not everything could be restored'}
        </h2>
        {seamNotices.map((notice) => (
          <div key={notice.sectie}>
            <p role="alert" className="pod-toegangmelding__tekst">
              {language === 'nl'
                ? `Het formulier is opnieuw geladen. In ${sectionTitle(notice.sectie)} had u iets getypt dat nog niet was opgeslagen, terwijl iemand anders intussen in hetzelfde veld schreef: ${notice.velden.map((id) => fieldLabel(notice.sectie, id)).join(', ')}. Daar staat nu de tekst van de ander.`
                : `The form was reloaded. In ${sectionTitle(notice.sectie)} you had typed something that was not saved yet, while someone else wrote in the same field: ${notice.velden.map((id) => fieldLabel(notice.sectie, id)).join(', ')}. That field now shows the other person's text.`}
            </p>
            {notice.tekst === '' ? null : (
              <CopyBox
                taal={language}
                label={language === 'nl' ? `Wat u in ${sectionTitle(notice.sectie)} typte en nog niet was opgeslagen` : `What you typed in ${sectionTitle(notice.sectie)} and had not been saved yet`}
                uitleg={language === 'nl' ? 'Alleen op dit scherm, niet verstuurd. Kopieer het als u het wilt bewaren.' : 'Only on this screen, not sent. Copy it if you want to keep it.'}
                tekst={notice.tekst}
              />
            )}
          </div>
        ))}
      </section>
    )
  /** Een sectie die van generatie wisselt, is even alleen-lezen (`switchingSections`). */
  const shownProjection =
    switchingSections.length === 0
      ? connection.projectie
      : {
          ...connection.projectie,
          secties: connection.projectie.secties.map((section) => (switchingSections.includes(section.id) && section.recht === 'bewerken' ? { ...section, recht: 'lezen' as const } : section)),
        }
  const accessNotice =
    accessChanges.length === 0 ? undefined : (
      <section className="pod-toegangmelding" lang={language} data-proef="toegang-gewijzigd" aria-labelledby="pod-toegangmelding-kop">
        <h2 id="pod-toegangmelding-kop" className="pod-toegangmelding__kop">
          {language === 'nl' ? 'Uw toegang is gewijzigd' : 'Your access has changed'}
        </h2>
        <p role="alert" className="pod-toegangmelding__tekst">
          {gone.length === 0
            ? ''
            : language === 'nl'
              ? `U kunt niet meer werken in ${gone.map((change) => sectionTitle(change.sectie)).join(', ')}. De andere secties werken door. `
              : `You can no longer work in ${gone.map((change) => sectionTitle(change.sectie)).join(', ')}. The other sections keep working. `}
          {reading.length === 0
            ? ''
            : language === 'nl'
              ? `U kunt ${reading.map((change) => sectionTitle(change.sectie)).join(', ')} nu alleen lezen.`
              : `You can now only read ${reading.map((change) => sectionTitle(change.sectie)).join(', ')}.`}
        </p>
        {accessChanges
          .filter((change) => change.tekst !== '')
          .map((change) => (
            <CopyBox
              key={change.sectie}
              taal={language}
              label={language === 'nl' ? `Wat u in ${sectionTitle(change.sectie)} typte en nog niet was opgeslagen` : `What you typed in ${sectionTitle(change.sectie)} and had not been saved yet`}
              uitleg={language === 'nl' ? 'Alleen op dit scherm, niet verstuurd. Kopieer het als u het wilt bewaren.' : 'Only on this screen, not sent. Copy it if you want to keep it.'}
              tekst={change.tekst}
            />
          ))}
        {gone.length === 0 ? null : (
          <BusyButton
            className="ef-actieknop"
            data-soort="secundair"
            data-proef="opnieuw-laden"
            busyText={language === 'nl' ? 'Bezig met laden…' : 'Loading…'}
            onAction={async () => {
              try {
                await connection.save()
              } finally {
                setReload((counter) => counter + 1)
              }
            }}
          >
            {language === 'nl' ? 'Formulier opnieuw laden' : 'Reload the form'}
          </BusyButton>
        )}
      </section>
    )

  /**
   * Secties die wachten op de grens per gebruiker (9.2, opdracht van 1 november 2026): de pod weigerde
   * hun document met `te-veel-verzoeken`, en de verbinding probeert het opnieuw. Boven het formulier en
   * ook boven het wachtscherm, zodat een sectie nooit zonder uitleg leeg blijft of het formulier nooit
   * zonder uitleg blijft wachten (valkuil 6). Verdwijnt zodra elke sectie er is.
   */
  // De statusregio staat er altijd, ook leeg: een schermlezer leest haar voor als haar inhoud
  // verandert, niet als ze er met inhoud en al bij komt (zelfde reden als `statusLine`).
  const waitingNotice = (
    <div role="status" lang={language}>
      {waitingSections.length === 0 ? null : (
        <p className="pod-wachtmelding" data-proef="sectie-wacht">
          {sectionsWaitingNotice(language, waitingSections.map(sectionTitle), waitingSections.length >= connection.documenten.size)}
        </p>
      )}
    </div>
  )

  return (
    <>
      {sessionNotice}
      {warningNotice}
      {adminBar}
      {waitingNotice}
      {/*
        Eén kopbalk: merk en kruimelpad als slot vooraan in de balk van het formulier, delen
        achteraan (ontwerp van het invulscherm, 29 oktober 2026). Delen en het overzicht van de
        delingen (R-64, R-66) pas met de projectie, want die zegt of deze persoon mag delen en welke
        secties hij ziet.
      */}
      <FillForm
        onDocumentTitle={setDocumentTitle}
        sessieVerlopen={sessionExpired}
        barStart={
          <BrandAndPath
            taal={language}
            {...(contextInfo.bloknaam === undefined ? {} : { bloknaam: contextInfo.bloknaam })}
            instantieTitle={connection.projectie.titel[language]}
          />
        }
        delen={
          adviser !== undefined ? undefined : <ShareButton
            taal={language}
            instantieId={current.instantieId}
            // Na indienen geeft een deling hooguit lezen van de bevroren beurt, en meeschrijven niets: dan
            // alleen "Wie heeft toegang" (audit delen G-25). De volgende beurt is een nieuwe instantie.
            projectie={connection.modus === 'nalezen' ? { ...connection.projectie, magDelen: false } : connection.projectie}
            headers={headersFor(current)}
            verborgenSecties={hiddenSections}
            openWith={shareStart}
            onOpenWith={setShareStart}
            onSessionExpired={expireSession}
            advies={advice.forDialog}
            onAdviesGewijzigd={advice.refresh}
            {...(advice.sidePanel === undefined ? {} : { onBekijkAdvies: advice.showRequest })}
          />
        }
        {...(adviser !== undefined
          ? {
              advies: { kanVragen: false, aantal: () => 0, onVragen: () => undefined, onToon: () => undefined, ...(adviser.anker === undefined ? {} : { vast: adviser.anker }) },
              ...(adviserPanel === undefined ? {} : { zijpaneel: adviserPanel }),
            }
          : {
              ...(advice.marks === undefined ? {} : { advies: advice.marks }),
              ...(advice.sidePanel === undefined ? {} : { zijpaneel: advice.sidePanel }),
            })}
        onHiddenSections={setHiddenSections}
        uitloggen={<LogoutButton taal={language} persoon={current.persoon} {...(sessionEntries === undefined ? {} : { ingangen: sessionEntries })} />}
        naamVan={nameOf}
        {...(sharedEntry === undefined ? {} : { deling: sharedEntry })}
        {...(accessNotice === undefined && seamNotice === undefined
          ? {}
          : {
              toegangMelding: (
                <>
                  {accessNotice}
                  {seamNotice}
                </>
              ),
            })}
        // Een opmerking bij een leessectie langs de pod (R-73, besluit 8); niet op de adviseurpagina, die
        // het adviesgesprek heeft. Per sectie beslist `magOpmerken` in de projectie (R-184b, R-66b): wie een
        // sectie alleen langs een opening door beheer of langs zijn adviesverzoek leest, krijgt daar geen
        // knop, ook als hij elders een eigen toekenning heeft. Dezelfde berekening als de weigering van de pod.
        {...(adviser !== undefined
          ? {}
          : {
              onOpmerking: (request: CommentRequest) =>
                postComment({ instantieId: current.instantieId, headers: headersFor(current), request, fetch: (input, init) => globalThis.fetch(input, init), onSessionExpired: expireSession }),
              onOpmerkingVerwijderen: (request: CommentDeleteRequest) =>
                deleteComment({ instantieId: current.instantieId, headers: headersFor(current), request, fetch: (input, init) => globalThis.fetch(input, init), onSessionExpired: expireSession }),
              // Een akkoord per persoon langs de pod (eisenboek 12.56); de pod weigert wie de sectie niet mag bewerken.
              onAkkoord: (request: { readonly sectie: string; readonly veld: string; readonly handeling: 'geven' | 'intrekken' }) =>
                postConsent({ instantieId: current.instantieId, headers: headersFor(current), request, fetch: (input, init) => globalThis.fetch(input, init), onSessionExpired: expireSession }),
            })}
        projectie={shownProjection}
        mode={connection.modus}
        taal={language}
        onLanguage={onLanguage}
        documenten={connection.documenten}
        verwijzingen={connection.verwijzingen}
        identiteit={connection.identiteit}
        instantieId={current.instantieId}
        nu={() => new Date().toISOString().replace(/\.\d{3}Z$/, 'Z')}
        save={connection.save}
        gereed={state === 'gereed'}
        connections={connection.verbindingen}
        prefill={current.voorvulling}
        uploadAttachment={uploadAttachment}
        bestanden={files}
        onUnknownFiles={onUnknownFiles}
        {...contextInfo}
        {...(work === undefined
          ? {}
          : {
              werkverzameling: work.werkverzameling,
              comments: work.opmerkingen,
              changedFields: work.gewijzigdeVelden,
              previousOutcomes: work.vorigeUitkomsten,
              uitkomsten: Object.fromEntries(
                (work.uitkomsten ?? []).map((outcome) => [outcome.criteriumId, outcome]),
              ),
            })}
        /**
         * Het oordeel van deze lezer bij één criterium (R-147, R-152).
         *
         * Dezelfde weg als indienen en om dezelfde reden: een schrijfactie gaat langs de pod en
         * niet langs de samenwerklaag, want een oordeel staat haaks op de antwoorden (U23) en
         * mag maar één keer (U21, R-192). De CSRF-header hoort erbij zoals bij elke schrijfactie
         * met het cookie.
         *
         * Na afloop wordt de werkverzameling opnieuw gehaald in plaats van de uitkomst uit het
         * antwoord te plakken. Dat is een verzoek meer en het is het waard: de pod vult de
         * toetser, het moment en de revisie zelf (R-154, R-175), dus wat er staat is wat de pod
         * ervan heeft gemaakt en niet wat deze pagina dacht te sturen.
         */
        onVerdict={(criterionId, verdict) =>
          (async () => {
            try {
              const response = await fetch(`/criteria/${encodeURIComponent(criterionId)}`, {
                method: 'PUT',
                headers: {
                  ...headersFor(current),
                  ...WRITE_HEADERS,
                },
                body: JSON.stringify({ instantie: current.instantieId, ...verdict }),
              })
              if (response.status === 201) {
                setRejection(undefined)
                // Lukt het halen niet, dan blijft staan wat er stond: het oordeel is vastgelegd, en een
                // lege werkverzameling zou de uitkomsten van het scherm halen alsof er geen oordeel is.
                const fetched = await getWerkverzameling(current)
                if (fetched !== undefined) setWork(fetched)
                return
              }
              if (isSessionExpiredResponse(response.status)) {
                setVerdictLost(true)
                expireSession()
                reject(await rejectionOf('PUT', `/criteria/${criterionId}`, response))
                return
              }
              reject(await rejectionOf('PUT', `/criteria/${criterionId}`, response))
            } catch (error) {
              reject(rejectionWithoutResponse('PUT', `/criteria/${criterionId}`, error))
            }
          })()
        }
        {...(position === undefined
          ? {}
          : {
              series: position,
              seriesBusy: jumping,
              /**
               * De sprong naar een ander dossier, en de hele eis zit in de volgorde (R-122).
               *
               * Eerst alles wegschrijven en pas dan wisselen. Dat is dezelfde regel als bij een
               * pod die stopt: een stopsignaal sluit de verbindingen en schrijft eerst weg. Zou
               * het andersom gaan, dan ruimt het effect de sockets op terwijl er nog iets in het
               * document staat dat de pod niet heeft, en dat is precies het invoerverlies dat de
               * eis verbiedt.
               *
               * Mislukt het wegschrijven, dan blijft dit dossier staan met de melding erbij, en
               * het formulier blijft staan: het is er nog en de invoer ook. Dat is beter dan
               * doorspringen: wie doorspringt is zijn invoer kwijt en weet dat niet.
               */
              onNavigate: (destination: string) => {
                void (async () => {
                  setJumping(true)
                  try {
                    await connection.save()
                    setInstantieId(destination)
                  } catch (error) {
                    setJumping(false)
                    // Bij een verlopen sessie staat die melding er al, en blijft dit dossier staan.
                    if (error instanceof SessionExpiredError) {
                      reject(SESSION_EXPIRED_CODE)
                      return
                    }
                    logRejection({ handeling: 'wissel', naar: destination, fout: error })
                    reject(SWITCH_NOT_STORED)
                  }
                })()
              },
            })}
        // De adviseur leest de stand waarover de vraag gaat, zonder eerdere versies en zonder de
        // melding voor de aanvrager.
        {...(versies.length === 0 || adviser !== undefined ? {} : { earlierVersies: versies })}
        {...(versiemomenten === undefined || adviser !== undefined
          ? {}
          : {
              versiemomenten,
              onVersiemomenten: () => {
                void getVersiemomenten(current).then((fetched) => fetched !== undefined && setVersiemomenten(fetched))
              },
            })}
        {...(adviser === undefined ? {} : { submittedNotice: false })}
        {...(earlierValues === undefined ? {} : { eerdereWaarden: earlierValues })}
        {...(chosenVersie === undefined ? {} : { chosenVersie })}
        onEarlierVersie={(revisieId) => {
          setChosenVersie(revisieId)
          if (revisieId === undefined) {
            setEarlierValues(undefined)
            return
          }
          void (async () => {
            const values = await getVersie(current, revisieId)
            // Komt hij niet, dan blijft de keuze staan en de waarden weg: dan staat er niets
            // naast de vragen, en dat is beter dan de waarden van een andere versie laten staan.
            setEarlierValues(values)
          })()
        }}
        // De beloften gaan terug naar de knop, zodat die bezig staat tot de pod antwoordt
        // (`BusyButton`). `closeBeurt` vangt elke fout zelf af en toont de melding.
        onSubmit={() => closeBeurt(connection, '/indienen', 'ingediend')}
        onHandover={(eventType: ReviewHandover) =>
          closeBeurt(connection, `/overdrachten/${encodeURIComponent(eventType)}`, eventType)
        }
      />
      {/*
        Direct onder de actiebalk (`.ef-schilvoet`, de laatste kaart van het formulier), want
        daar zit de knop die deze melding veroorzaakte. `role="alert"` zodat een schermlezer hem
        voorleest zonder dat de focus verspringt; de `key` maakt van een tweede, gelijke weigering
        een nieuwe melding die opnieuw wordt voorgelezen. Hij schuift zelf in beeld (het effect bij
        `rejectionNotice` hierboven).
      */}
      {rejection === undefined ? null : (
        <p
          key={rejection.volgnummer}
          ref={rejectionNotice}
          className="pod-weigering"
          role="alert"
          data-proef="weigering"
          lang={language}
        >
          {noticeForRejection(rejection.code, language)}
        </p>
      )}
      {statusLine}
    </>
  )
}

/** De paginatitel uit het formulier (WCAG 2.4.2); `index.html` zegt tot dan "Formulier". */
function setDocumentTitle(title: string): void {
  document.title = title
}

/** Hoe lang vóór de vervaltijd de waarschuwing komt: ruim de twintig seconden van WCAG 2.2.1. */
/**
 * Terug naar de schuifpositie van vóór een verlenging (WCAG 2.2.1, R-139).
 *
 * Niet één `scrollTo`: als het formulier opkomt is de pagina nog niet op haar volle lengte (secties en
 * editors bouwen zich daarna op), en dan klemt de browser de positie op wat er al staat. Daarom per
 * frame opnieuw, tot de positie twee seconden lang staat of de tijd om is, en meteen stoppen als de
 * invuller zelf scrolt, typt of tikt: dan is zijn eigen plek belangrijker dan de bewaarde.
 */
function restoreScroll(place: number): void {
  const until = Date.now() + 2_000
  let stopped = false
  const stop = (): void => {
    stopped = true
  }
  const events = ['wheel', 'keydown', 'touchstart', 'mousedown'] as const
  for (const event of events) window.addEventListener(event, stop, { once: true, passive: true })
  const step = (): void => {
    if (stopped || Date.now() > until) {
      for (const event of events) window.removeEventListener(event, stop)
      return
    }
    if (Math.abs(window.scrollY - place) > 2) window.scrollTo(0, place)
    requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
}

const SESSION_WARNING_MS = 5 * 60 * 1000
/** Hoe lang "Sessie verlengen" wacht tot alles is opgeslagen; een normale synchronisatie duurt een tiende seconde. */
const RENEW_WAIT_MS = 10_000
const RENEW_NOT_SAVED = {
  nl: 'Nog niet alles is opgeslagen, dus verlengen kan nog niet. Controleer de verbinding en probeer het opnieuw.',
  en: 'Not everything has been saved yet, so the session cannot be extended yet. Check the connection and try again.',
} as const
/** De grootste wachttijd die `setTimeout` aankan (2^31 - 1 milliseconden). */
const MAX_TIMER_MS = 2_147_483_647

/** De zin en de knoppen om aan de andere kant in te loggen; zonder ingangen van de pod alleen de zin. */
function AnotherSide({ tekst, taal: language, opties: options }: { readonly tekst: string; readonly taal: Language; readonly opties: readonly LoginOption[] }): ReactNode {
  return (
    <div className="pod-geentoegang__anderekant" data-proef="andere-kant">
      <p>{tekst}</p>
      {options.length === 0 ? null : <LoginChoice taal={language} opties={options} />}
    </div>
  )
}
